/* TikiLand-owned SMS offer UI. No vendor runtime, network calls or subscription API. */
(function (scope) {
  'use strict';
  const PHONE = '+18333400035';
  const KEYWORD = '5off';
  function normalizePhone(value) {
    if (typeof value !== 'string' && typeof value !== 'number') return '';
    const raw = String(value).trim();
    if (!raw || /[\u0000-\u001f\u007f]/.test(raw) || !/^[+\d().\-\s]+$/.test(raw)) return '';
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+')) return /^\+[^+]*$/.test(raw) && /^[1-9]\d{7,14}$/.test(digits) ? '+' + digits : '';
    if (/^[2-9]\d{9}$/.test(digits)) return '+1' + digits;
    if (/^1[2-9]\d{9}$/.test(digits)) return '+' + digits;
    return '';
  }
  function normalizeKeyword(value) {
    if (typeof value !== 'string' && typeof value !== 'number') return '';
    const keyword = String(value).trim();
    if (!keyword || keyword.length > 160 || /[\u0000-\u001f\u007f]/.test(keyword)) return '';
    try { encodeURIComponent(keyword); return keyword; } catch (_) { return ''; }
  }
  function displayPhone(phone) {
    return /^\+1\d{10}$/.test(phone) ? '(' + phone.slice(2,5) + ') ' + phone.slice(5,8) + '-' + phone.slice(8) : phone;
  }
  function safeLink(value, contacts = false) {
    if (typeof value !== 'string') return '';
    const raw = value.trim();
    if (!raw || /[\u0000-\u0020\u007f<>"`\\]/.test(raw)) return '';
    if (raw.startsWith('#')) return raw;
    if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
    try {
      const url = new URL(raw);
      if (['https:', 'http:'].includes(url.protocol) && url.hostname && !url.username && !url.password) return url.href;
      if (contacts && ['mailto:', 'tel:'].includes(url.protocol)) return url.href;
    } catch (_) { /* Malformed or unsupported links stay non-clickable. */ }
    return '';
  }
  function escapeHtml(value) {
    return String(value ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }
  function plainToRichText(value) {
    const text = String(value ?? '');
    return text ? '<p>' + escapeHtml(text).replace(/\r\n?|\n/g,'<br />') + '</p>' : '';
  }
  const COPY_KEYS = ['sms_phone','sms_keyword','launcher_text','headline','description','cta_text','cta_subline','instructions','fallback_copy','eligibility_copy','consent_copy','terms_label','terms_url'];
  function migrateCopySettings(settings, defaults, oldTypes = {}) {
    const migrated = { ...settings };
    for (const key of COPY_KEYS) if (!Object.hasOwn(migrated,key)) migrated[key] = defaults[key];
    for (const key of ['headline','terms_label','cta_subline']) {
      if (Object.hasOwn(settings,key) && ['text','textarea'].includes(oldTypes[key])) migrated[key] = escapeHtml(settings[key]);
    }
    for (const key of ['description','instructions','fallback_copy','eligibility_copy','consent_copy']) {
      if (Object.hasOwn(settings,key) && ['text','textarea'].includes(oldTypes[key])) migrated[key] = plainToRichText(settings[key]);
    }
    return migrated;
  }
  const list = value => String(value || '').split(/[\n,]/).map(v => v.trim()).filter(Boolean);
  const clamp = (value, min, max, fallback) => Number.isFinite(Number(value)) ? Math.min(max, Math.max(min, Number(value))) : fallback;
  const normalizePath = path => (String(path || '/').split(/[?#]/)[0].replace(/\/+$/, '') || '/');
  const PAGE_TYPES = ['index', 'product', 'collection', 'list-collections', 'page', 'search', 'blog', 'article', 'metaobject', 'policy', '404', 'cart'];
  const typeKey = type => 'page_type_' + type.replace(/-/g, '_');
  function selectedPageTypes(config) {
    if (config.page_types_mode === 'all') return null;
    if (config.page_types_mode === 'selected') {
      return PAGE_TYPES.filter(type => config[typeKey(type)] === true).concat(list(config.page_types_extra));
    }
    const legacy = list(config.include_page_types);
    return legacy.length ? legacy : null;
  }
  function migratePageTypeSettings(settings) {
    if (['all', 'selected'].includes(settings.page_types_mode)) return { ...settings };
    const legacy = list(settings.include_page_types);
    const migrated = { ...settings, page_types_mode: legacy.length ? 'selected' : 'all' };
    for (const type of PAGE_TYPES) migrated[typeKey(type)] = legacy.length ? legacy.includes(type) : true;
    migrated.page_types_extra = legacy.filter(type => !PAGE_TYPES.includes(type)).join(',');
    delete migrated.include_page_types;
    return migrated;
  }
  function matchesPath(path, pattern) {
    if (!pattern.startsWith('/')) return false;
    const escaped = normalizePath(pattern).replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    return new RegExp('^' + escaped + '$').test(normalizePath(path));
  }
  function pageAllowed(config, path, pageType) {
    path = normalizePath(path);
    // Checkout is never an acquisition surface, including accidental future mounts.
    if (/^\/(checkout|checkouts)(\/|$)/.test(path)) return false;
    if (config.disable_cart && (pageType === 'cart' || /^\/cart(\/|$)/.test(path))) return false;
    const types = selectedPageTypes(config);
    if (types !== null && !types.includes(pageType)) return false;
    const includes = list(config.include_paths);
    if (includes.length && !includes.some(pattern => matchesPath(path, pattern))) return false;
    return !list(config.exclude_paths).some(pattern => matchesPath(path, pattern));
  }
  function smsHref(userAgent, touchPoints, config = {}) {
    const ios = /iPhone|iPad|iPod/.test(userAgent || '') || (/Macintosh/.test(userAgent || '') && touchPoints > 1);
    const phone = normalizePhone(config.sms_phone === undefined ? PHONE : config.sms_phone);
    const keyword = normalizeKeyword(config.sms_keyword === undefined ? KEYWORD : config.sms_keyword);
    return phone && keyword ? 'sms:' + phone + (ios ? '&' : '?') + 'body=' + encodeURIComponent(keyword) : '';
  }
  function autoReady(config, elapsedSeconds, views, scrollPercent, autoUntil, now) {
    return config.trigger_mode === 'timed' && now >= autoUntil &&
      elapsedSeconds >= clamp(config.delay_seconds, 0, 120, 20) &&
      views >= clamp(config.min_pageviews, 1, 10, 1) &&
      scrollPercent >= clamp(config.min_scroll, 0, 100, 0);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { matchesPath, pageAllowed, smsHref, autoReady, PAGE_TYPES, typeKey, selectedPageTypes, migratePageTypeSettings, normalizePhone, normalizeKeyword, displayPhone, safeLink, escapeHtml, plainToRichText, COPY_KEYS, migrateCopySettings };
  if (!scope || !scope.document || scope.TikiLandSmsSignup) return;

  const document = scope.document;
  const draftTheme = scope.Shopify?.theme?.role && scope.Shopify.theme.role !== 'main';
  const storageSuffix = draftTheme ? '_preview_' + String(scope.Shopify.theme.id || 'local') : '';
  const STORAGE_KEY = 'tl_sms_offer_v1' + storageSuffix;
  const VIEWS_KEY = 'tl_sms_pageviews_v1' + storageSuffix;
  const SUBSCRIBER_KEY = 'tl_sms_subscriber_hide_v1' + storageSuffix;
  const instances = new Map();
  let visits = 1;
  let visitsRestored = false;
  function preferencePermission() {
    try {
      const privacy = scope.Shopify?.customerPrivacy;
      return typeof privacy?.preferencesProcessingAllowed === 'function' ? privacy.preferencesProcessingAllowed() === true : null;
    } catch (_) { return null; }
  }
  function saveVisits() {
    if (preferencePermission() !== true) return;
    try {
      if (!visitsRestored) {
        const previous = parseInt(scope.sessionStorage.getItem(VIEWS_KEY),10);
        visits += Number.isFinite(previous) && previous >= 0 ? previous : 0;
        visitsRestored = true;
      }
      scope.sessionStorage.setItem(VIEWS_KEY,String(visits));
    } catch (_) { /* Page-local thresholds work without storage. */ }
  }

  function visible(element) {
    if (!element || !element.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    if (element.closest('[hidden], [aria-hidden="true"]')) return false;
    const rect = element.getBoundingClientRect();
    const style = scope.getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < scope.innerHeight && rect.left < scope.innerWidth &&
      style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }
  function queryVisible(root, selector, ownRoot) {
    try { return Array.from(root.querySelectorAll(selector)).some(el => !ownRoot.contains(el) && visible(el)); }
    catch (_) { return false; } // Invalid optional custom selectors cannot break shopping.
  }
  function inferredPageType(path, initialPath, initialType) {
    if (normalizePath(path) === normalizePath(initialPath)) return initialType;
    if (/^\/products\//.test(path)) return 'product';
    if (/^\/collections\//.test(path)) return 'collection';
    if (/^\/pages\//.test(path)) return 'page';
    if (/^\/cart(?:\/|$)/.test(path)) return 'cart';
    if (/^\/search(?:\/|$)/.test(path)) return 'search';
    if (path === '/') return 'index';
    return 'other';
  }

  function richFragment(value, tokens, inline = false, links = true) {
    // Parse only in an inert template. Never insert unreviewed markup into the live DOM.
    const template = document.createElement('template');
    template.innerHTML = String(value ?? '');
    const allowed = new Set(inline ? ['strong','b','em','i','u','span'] : ['p','br','strong','b','em','i','u','span','ul','ol','li']);
    if (links) allowed.add('a');
    const discard = new Set(['script','style','template','iframe','object','embed','svg','math','link','meta','form','input','button','img','video','audio','source']);
    const output = document.createDocumentFragment();
    function copy(node, parent) {
      if (node.nodeType === 3) {
        parent.append(document.createTextNode(node.nodeValue.replace(/\[(phone|keyword_upper|keyword|headline)\]/g,(match,key)=>Object.hasOwn(tokens,key) ? tokens[key] : match)));
        return;
      }
      if (node.nodeType !== 1) return;
      const tag = node.localName.toLowerCase();
      if (discard.has(tag)) return;
      if (!allowed.has(tag)) { node.childNodes.forEach(child=>copy(child,parent)); return; }
      const href = tag === 'a' ? safeLink(node.getAttribute('href'),true) : '';
      if (tag === 'a' && !href) { node.childNodes.forEach(child=>copy(child,parent)); return; }
      const element = document.createElement(tag);
      if (href) { element.setAttribute('href',href); element.setAttribute('rel','noopener noreferrer'); }
      node.childNodes.forEach(child=>copy(child,element));
      parent.append(element);
    }
    template.content.childNodes.forEach(node=>copy(node,output));
    return output;
  }

  class Signup {
    constructor(root) {
      this.root = root;
      this.id = root.id;
      try { this.config = JSON.parse(root.querySelector('[data-tl-sms-config]').textContent); }
      catch (_) { return; } // The HTML fallback remains if configuration cannot initialize.
      this.launcher = root.querySelector('[data-tl-sms-launcher]');
      this.dialog = root.querySelector('[data-tl-sms-dialog]');
      this.openButton = root.querySelector('[data-tl-sms-open]');
      this.previousFocus = null;
      this.createdAt = Date.now();
      this.path = scope.location.pathname;
      this.initialPath = this.path;
      this.autoAttempted = false;
      this.launcherDismissed = false;
      this.holds = new Set();
      this.unsubscribers = [];
      this.media = scope.matchMedia('(max-width: 749px)');
      this.previewMode = Boolean(this.config.editor_mode || scope.Shopify?.designMode ||
        (draftTheme && new URLSearchParams(scope.location.search).get('tl_sms_test') === '1'));
      this.previewOverride = false;
      this.subscriberMemoryUntil = 0;
      this.subscriberStoredUntil = 0;
      this.privacyState = undefined;
      this.renderCopy();
      this.nativeDialog = typeof this.dialog.showModal === 'function';
      if (!this.nativeDialog) {
        // A manual text-to-join footer is safer than pretending an unsupported browser has a modal.
        if (!this.allowed()) root.querySelector('[data-tl-sms-fallback]').hidden = true;
        root.dataset.ready = 'fallback';
        this.config = null;
        return;
      }
      this.saved = this.config.editor_mode || scope.Shopify?.designMode ? { autoUntil: 0, hideUntil: 0 } : this.readDismissals();
      this.listen(this.openButton, 'click', () => this.open(false));
      this.listen(root.querySelector('[data-tl-sms-launcher-close]'), 'click', () => this.dismissLauncher());
      this.listen(root.querySelector('[data-tl-sms-close]'), 'click', () => this.close('dismiss'));
      const subscriberButton = root.querySelector('[data-tl-sms-subscriber-hide]');
      if (subscriberButton) subscriberButton.hidden = !this.config.subscriber_hide_enabled || !String(this.config.subscriber_hide_text || '').trim();
      this.listen(subscriberButton,'click',()=>this.hideAsSubscriber());
      this.listen(document,'visitorConsentCollected',()=>{this.syncPrivacy();this.refresh();});
      this.listen(this.dialog, 'cancel', event => { event.preventDefault(); this.close('dismiss'); });
      this.listen(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close('dismiss');
      });
      this.listen(this.dialog, 'keydown', event => this.handleKeyboard(event));
      this.listen(document, 'tl:sms-signup:suppress', event => {
        const detail = event.detail || {};
        if (typeof detail.key !== 'string' || !detail.key || detail.key.length > 80) return;
        if (detail.active) this.holds.add(detail.key); else this.holds.delete(detail.key);
        this.refresh();
      });
      this.listen(scope, 'scroll', () => this.schedule(), { passive: true });
      this.listen(scope, 'resize', () => this.schedule(), { passive: true });
      this.listen(scope, 'popstate', () => this.refresh());
      this.listen(scope, 'hashchange', () => this.refresh());
      this.listen(scope, 'pageshow', () => this.refresh());
      this.listen(scope, 'storage', event => {
        if (event.key === STORAGE_KEY) { this.saved = this.readDismissals(); this.refresh(); }
        if (event.key === SUBSCRIBER_KEY) { this.subscriberStoredUntil = this.readSubscriberPreference(); this.refresh(); }
      });
      const mediaChange = () => this.refresh();
      this.media.addEventListener('change', mediaChange);
      this.unsubscribers.push(() => this.media.removeEventListener('change', mediaChange));
      this.observer = new MutationObserver(records => {
        if (records.every(record => this.root.contains(record.target))) return;
        this.schedule();
      });
      this.observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'aria-expanded', 'style'] });
      // Also catches history.pushState navigation and opaque app-host geometry changes.
      this.interval = scope.setInterval(() => this.refresh(), 1000);
      const tools = root.querySelector('[data-tl-sms-editor-tools]');
      if (tools) {
        tools.hidden = !this.previewMode;
        this.listen(tools.querySelector('[data-tl-sms-reset]'), 'click', () => this.reset());
        this.listen(tools.querySelector('[data-tl-sms-preview]'), 'click', () => { this.reset(); this.previewOverride = true; this.open(false); });
      }
      root.dataset.ready = 'true';
      this.refresh();
    }
    renderCopy() {
      const config = this.config;
      const phone = normalizePhone(config.sms_phone === undefined ? PHONE : config.sms_phone);
      const keyword = normalizeKeyword(config.sms_keyword === undefined ? KEYWORD : config.sms_keyword);
      const tokens = { phone: displayPhone(phone) || String(config.sms_phone ?? ''), keyword: keyword || String(config.sms_keyword ?? ''), keyword_upper: (keyword || String(config.sms_keyword ?? '')).toUpperCase(), headline: '' };
      tokens.headline = richFragment(config.headline,tokens,true).textContent;
      this.root.querySelectorAll('[data-tl-sms-rich]').forEach(element=>{
        const key = element.dataset.tlSmsRich;
        element.replaceChildren(richFragment(config[key],tokens, ['headline','cta_subline','terms_label'].includes(key), !['cta_subline','terms_label'].includes(key)));
      });
      const href = smsHref(scope.navigator.userAgent,scope.navigator.maxTouchPoints,config);
      this.root.dataset.contactValid = href ? 'true' : 'false';
      this.root.querySelectorAll('[data-tl-sms-send]').forEach(link=>{
        if (href) { link.setAttribute('href',href); link.removeAttribute('aria-disabled'); }
        else { link.removeAttribute('href'); link.setAttribute('aria-disabled','true'); }
        this.listen(link,'click',event=>{if (!href) event.preventDefault();});
      });
      const terms = safeLink(config.terms_url);
      this.root.querySelectorAll('[data-tl-sms-terms]').forEach(link=>{
        if (terms) { link.setAttribute('href',terms); link.removeAttribute('aria-disabled'); }
        else { link.removeAttribute('href'); link.setAttribute('aria-disabled','true'); }
      });
    }
    listen(target, name, handler, options) {
      if (!target) return;
      target.addEventListener(name, handler, options);
      this.unsubscribers.push(() => target.removeEventListener(name, handler, options));
    }
    readDismissals() {
      if (preferencePermission() !== true) return { autoUntil: 0, hideUntil: 0 };
      try {
        const value = JSON.parse(scope.localStorage.getItem(STORAGE_KEY) || '{}');
        return { autoUntil: Number(value.autoUntil) || 0, hideUntil: Number(value.hideUntil) || 0 };
      } catch (_) { return { autoUntil: 0, hideUntil: 0 }; }
    }
    saveDismissals() {
      if (this.config.editor_mode || scope.Shopify?.designMode || preferencePermission() !== true) return;
      try { scope.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.saved)); } catch (_) { /* In-memory fallback. */ }
    }
    cooldownUntil() { return Date.now() + clamp(this.config.cooldown_hours, 0, 168, 24) * 3600000; }
    syncPrivacy() {
      const permission = preferencePermission();
      if (permission === this.privacyState) return;
      this.privacyState = permission;
      if (this.config.editor_mode || scope.Shopify?.designMode) return;
      if (permission === true) {
        saveVisits();
        const stored = this.readDismissals();
        this.saved.autoUntil = Math.max(this.saved.autoUntil,stored.autoUntil);
        this.saved.hideUntil = Math.max(this.saved.hideUntil,stored.hideUntil);
        this.subscriberStoredUntil = this.readSubscriberPreference();
      } else {
        this.subscriberStoredUntil = 0;
        if (permission === false) {
          try { scope.localStorage.removeItem(SUBSCRIBER_KEY); scope.localStorage.removeItem(STORAGE_KEY); scope.sessionStorage.removeItem(VIEWS_KEY); } catch (_) { /* Cleanup may also be unavailable. */ }
        }
      }
    }
    readSubscriberPreference() {
      if (preferencePermission() !== true || !this.config.subscriber_hide_enabled) return 0;
      try {
        const value = JSON.parse(scope.localStorage.getItem(SUBSCRIBER_KEY) || '{}');
        const now = Date.now();
        if (value.version === String(this.config.subscriber_reset_version ?? '1') && Number.isFinite(value.until) && value.until > now && value.until <= now + 366 * 86400000) return value.until;
        scope.localStorage.removeItem(SUBSCRIBER_KEY);
      } catch (_) { /* Only this page can remember the explicit choice. */ }
      return 0;
    }
    subscriberHidden() {
      return this.config.subscriber_hide_enabled && (Date.now() < this.subscriberMemoryUntil ||
        (preferencePermission() === true && Date.now() < this.subscriberStoredUntil));
    }
    hideAsSubscriber() {
      if (!this.config.subscriber_hide_enabled) return;
      const days = clamp(this.config.subscriber_hide_days,0,365,90);
      this.subscriberMemoryUntil = days ? Date.now() + days * 86400000 : Infinity;
      if (days && preferencePermission() === true && !this.config.editor_mode && !scope.Shopify?.designMode) {
        try {
          scope.localStorage.setItem(SUBSCRIBER_KEY,JSON.stringify({until:this.subscriberMemoryUntil,version:String(this.config.subscriber_reset_version ?? '1')}));
          this.subscriberStoredUntil = this.subscriberMemoryUntil;
        } catch (_) { /* Explicit choice still works for this page. */ }
      }
      this.close('subscriber');
      this.refresh();
      // The action removes its own dialog; return focus to the browsing document.
      const previousTabindex = document.body.getAttribute('tabindex');
      document.body.setAttribute('tabindex','-1');document.body.focus({preventScroll:true});
      if (previousTabindex === null) document.body.removeAttribute('tabindex'); else document.body.setAttribute('tabindex',previousTabindex);
    }
    blockers() {
      if (this.holds.size) return true;
      if (this.config.suppress_overlays) {
        // Debut's menu/search classes were verified in this store's assets/theme.js.
        if (document.documentElement.classList.contains('js-menu--is-open') ||
            document.body.classList.contains('js-menu--is-open') || document.body.classList.contains('js-drawer-open')) return true;
        if (queryVisible(document, '.mobile-nav-wrapper.js-menu--is-open, dialog[open], [role="dialog"][aria-modal="true"]', this.root)) return true;
        // This store's Shopify cookie banner body was verified in the actual preview DOM.
        if (queryVisible(document, '.shopify-pc__banner__body', this.root)) return true;
        if (this.config.overlay_selectors && queryVisible(document, this.config.overlay_selectors, this.root)) return true;
      }
      if (this.config.suppress_forms) {
        // Public, observable Forms hosts only. Never intercept or patch a vendor shadow root.
        const hosts = document.querySelectorAll('shopify-forms-embed, shopify-forms-modal, [data-shopify-forms-modal]');
        for (const host of hosts) {
          if (this.root.contains(host)) continue;
          if (host.shadowRoot && queryVisible(host.shadowRoot, '[role="dialog"][aria-modal="true"], dialog[open]', this.root)) return true;
          // A visible opaque host is treated conservatively as busy. Zero-size closed hosts cannot be inferred.
          if (visible(host) && host.getBoundingClientRect().height > 80) return true;
        }
      }
      return false;
    }
    allowed() {
      const config = this.config;
      const mobile = this.media.matches;
      const type = inferredPageType(scope.location.pathname, this.initialPath, config.page_type);
      return (!config.customer_excluded || this.previewOverride) && !this.subscriberHidden() && config.enabled &&
        (mobile ? config.mobile_enabled : config.desktop_enabled) && pageAllowed(config, scope.location.pathname, type);
    }
    place() {
      const prefix = this.media.matches ? 'mobile_' : 'desktop_';
      const position = this.config[prefix + 'position'];
      this.root.dataset.position = ['left', 'center', 'right'].includes(position) ? position : 'right';
      this.root.style.setProperty('--tl-sms-side', clamp(this.config[prefix + 'side'], 0, 120, 12) + 'px');
      // The Shopify draft bar is a top-layer popover and can cover an ordinary launcher.
      // Reserve its actual height only in draft previews; production placement stays configurable.
      const previewBar = draftTheme ? document.getElementById('PBarNextFrameWrapper') : null;
      const barRect = previewBar && visible(previewBar) ? previewBar.getBoundingClientRect() : null;
      const previewSpace = barRect && barRect.bottom >= scope.innerHeight - 2 ? barRect.height : 0;
      this.root.style.setProperty('--tl-sms-bottom', (clamp(this.config[prefix + 'bottom'], 0, 200, 12) + previewSpace) + 'px');
    }
    scrollPercent() {
      const available = document.documentElement.scrollHeight - scope.innerHeight;
      return available > 0 ? Math.min(100, Math.max(0, scope.scrollY / available * 100)) : 0;
    }
    schedule() {
      if (this.frame) return;
      this.frame = scope.requestAnimationFrame(() => { this.frame = null; this.refresh(); });
    }
    refresh() {
      if (!this.config || !this.root.isConnected) return;
      this.syncPrivacy();
      if (this.path !== scope.location.pathname) {
        this.path = scope.location.pathname;
        visits += 1;
        saveVisits();
        this.createdAt = Date.now();
        this.autoAttempted = false;
        this.close('navigation');
      }
      this.place();
      const blocked = this.blockers();
      const allowed = this.allowed();
      const dismissed = this.launcherDismissed || Date.now() < this.saved.hideUntil;
      this.root.dataset.suppressed = blocked ? 'true' : 'false';
      if ((!allowed || blocked || dismissed) && this.isOpen()) this.close('suppression');
      this.launcher.hidden = !allowed || blocked || dismissed || this.isOpen();
      if (allowed && !blocked && !dismissed && !this.isOpen() && !this.autoAttempted &&
        !this.config.editor_mode && !scope.Shopify?.designMode &&
        autoReady(this.config, (Date.now() - this.createdAt) / 1000, visits, this.scrollPercent(), this.saved.autoUntil, Date.now())) this.open(true);
    }
    isOpen() { return this.dialog.hasAttribute('open'); }
    open(automatic) {
      if (!this.allowed() || this.blockers() || this.isOpen() || this.launcherDismissed || Date.now() < this.saved.hideUntil) return false;
      this.previousFocus = document.activeElement;
      this.autoAttempted = true;
      this.launcher.hidden = true;
      try {
        if (this.nativeDialog) this.dialog.showModal();
        else { this.dialog.setAttribute('open', ''); this.dialog.setAttribute('aria-modal', 'true'); }
      } catch (_) { this.launcher.hidden = false; return false; }
      this.root.dataset.open = 'true';
      this.dialog.querySelector('[data-tl-sms-close]').focus({ preventScroll: true });
      return true;
    }
    close(reason) {
      if (!this.isOpen()) return;
      if (this.nativeDialog) this.dialog.close(); else this.dialog.removeAttribute('open');
      this.root.dataset.open = 'false';
      if (reason === 'dismiss' || reason === 'suppression') {
        this.saved.autoUntil = this.cooldownUntil();
        this.saveDismissals();
      }
      if (reason === 'dismiss') {
        this.refresh();
        const target = visible(this.previousFocus) ? this.previousFocus : (!this.launcher.hidden ? this.openButton : null);
        if (target) target.focus({ preventScroll: true });
      }
    }
    dismissLauncher() {
      this.launcherDismissed = true;
      this.saved.hideUntil = this.cooldownUntil();
      this.saved.autoUntil = this.saved.hideUntil;
      this.saveDismissals();
      this.close('suppression');
      this.refresh();
    }
    reset() {
      if (!this.previewMode) return false;
      this.close('reset');
      this.saved = { autoUntil: 0, hideUntil: 0 };
      this.subscriberMemoryUntil = 0;
      this.subscriberStoredUntil = 0;
      this.previewOverride = false;
      this.launcherDismissed = false;
      this.autoAttempted = false;
      this.createdAt = Date.now();
      try { scope.localStorage.removeItem(SUBSCRIBER_KEY); } catch (_) { /* Clear only this local preference, even in the editor. */ }
      if (!this.config.editor_mode && !scope.Shopify?.designMode) {
        try { scope.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* Optional storage. */ }
      }
      this.refresh();
      return true;
    }
    handleKeyboard(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close('dismiss'); return; }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')).filter(visible);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    destroy() {
      this.close('destroy');
      this.observer?.disconnect();
      scope.clearInterval(this.interval);
      if (this.frame) scope.cancelAnimationFrame(this.frame);
      this.unsubscribers.forEach(fn => fn());
      instances.delete(this.id);
    }
  }
  function mount(container) {
    container.querySelectorAll('[data-tl-sms-root]').forEach(root => {
      if (instances.has(root.id)) instances.get(root.id).destroy();
      const instance = new Signup(root);
      if (instance.config) instances.set(root.id, instance);
    });
  }
  scope.TikiLandSmsSignup = Object.freeze({
    // Local integration only; no customer identifiers or network data are accepted.
    suppress(key, active) { document.dispatchEvent(new CustomEvent('tl:sms-signup:suppress', { detail: { key, active: Boolean(active) } })); },
    reset() { return Array.from(instances.values()).map(instance => instance.reset()); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-tl-sms-root]').forEach(root => instances.get(root.id)?.destroy());
  });
})(typeof window === 'undefined' ? null : window);
